import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, ChevronRight, RefreshCcwDot, Telescope, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Explain } from '@/components/Explain';
import { JsonTree } from '@/components/JsonTree';
import { CopyId, Timestamp } from '@/components/primitives';
import { cn } from '@/lib/cn';
import type { Bootstrap, LookupParams } from '@/lib/bootstrap';
import { formatDuration, parseMysqlTime, type TimeZoneMode } from '@/lib/format';
import type { DelayDeliveryResult, DelayDeliveryTryRow } from '@/types';
import { ApiError, fetchClients } from '@/features/bet-inspection/lib/api';
import { EMPTY_FILTERS, FilterBar, toInput, type DeliveryFilters } from './components/FilterBar';
import { fetchDelayDeliveries } from './lib/api';
import { deliveryState, lastResult, payloadOf, type DeliveryState } from './lib/row';

/**
 * Delivery Retries — `game_aggr.delay_delivery_tries`.
 *
 * When a win or rollback cannot be delivered to the operator it goes to RMQ and
 * win-delivery-engine records each attempt here. This is the "the player was
 * never paid" screen, so it leads with the operator's own error code: that is
 * what separates "our side broke" from "they rejected it", and it is the single
 * thing support needs before escalating.
 */
export function DelayDelivery({
  boot,
  tz,
  onOpenBet,
}: {
  boot: Bootstrap;
  tz: TimeZoneMode;
  onOpenBet: (lookup: LookupParams) => void;
}) {
  const [filters, setFilters] = useState<DeliveryFilters>(EMPTY_FILTERS);

  const clients = useQuery({
    queryKey: ['clients', boot.api],
    queryFn: ({ signal }) => fetchClients({ api: boot.api!, token: boot.token, signal }),
    enabled: Boolean(boot.api),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const lookup = useMutation<DelayDeliveryResult, Error, DeliveryFilters>({
    mutationFn: (f) => fetchDelayDeliveries({ api: boot.api!, token: boot.token, input: toInput(f) }),
  });

  const result = lookup.data;

  return (
    <>
      <div className="sticky top-[41px] z-30 border-b border-hairline bg-bg/95 backdrop-blur">
        <FilterBar
          value={filters}
          onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
          onSearch={() => lookup.mutate(filters)}
          onReset={() => {
            setFilters((f) => ({ ...EMPTY_FILTERS, psid: f.psid }));
            lookup.reset();
          }}
          loading={lookup.isPending}
          clients={clients.data ?? []}
          clientsLoading={clients.isLoading}
          clientsError={clients.isError}
        />
      </div>

      {lookup.isPending && <Scanning />}
      {lookup.isError && <Failure error={lookup.error} />}
      {!lookup.isPending && !lookup.isError && !result && <Idle />}

      {result && !lookup.isPending && (
        <main className="rise">
          <ResolvedStrip result={result} onOpenBet={onOpenBet} />
          {result.rows.length === 0 ? (
            <Empty result={result} />
          ) : (
            <>
              <MisreadNote rows={result.rows} />
              <Table rows={result.rows} tz={tz} onOpenBet={onOpenBet} />
            </>
          )}
        </main>
      )}
    </>
  );
}

function ResolvedStrip({
  result,
  onOpenBet,
}: {
  result: DelayDeliveryResult;
  onOpenBet: (lookup: LookupParams) => void;
}) {
  const r = result.resolved;
  const pending = result.rows.filter((row) => deliveryState(row) === 'pending').length;
  const canOpen = Boolean(r.play_service_id && r.game_uuid && r.bet_id);

  return (
    <section className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-hairline bg-surface px-4 py-2.5">
      <Cell label="rows">
        <span className="tnum font-mono text-[13px] font-semibold text-text">
          {result.rows.length}
        </span>
        {result.truncated && (
          // Truncation is reported, never silent — a capped page must not read
          // as "this is all of it".
          <span className="ml-2 border border-warn/40 px-1 font-condensed text-[9px] uppercase tracking-wider text-warn">
            capped
          </span>
        )}
      </Cell>

      {result.rows.length > 0 && (
        <Cell label="undelivered">
          <span className={cn('tnum font-mono text-[13px]', pending ? 'text-crit' : 'text-ok')}>
            {pending}
          </span>
        </Cell>
      )}

      {r.matchedBy === 'recent' ? (
        <Cell label="scope">
          <span className="font-condensed text-[11px] uppercase tracking-wider text-text-dim">
            most recent
          </span>
        </Cell>
      ) : (
        <>
          <Cell label="matched by">
            <span className="font-condensed text-[11px] uppercase tracking-wider text-text-dim">
              {r.matchedBy.replace(/_/g, ' ')}
            </span>
          </Cell>
          {r.bet_id && (
            <Cell label={<Explain term="bet_id">bet</Explain>}>
              <CopyId value={r.bet_id} className="font-semibold" />
            </Cell>
          )}
          {r.cycle_uniq_id && (
            <Cell label={<Explain term="cycle_uniq_id">cycle</Explain>}>
              <CopyId value={r.cycle_uniq_id} truncate className="text-text-dim" />
            </Cell>
          )}
        </>
      )}

      {canOpen && (
        <button
          type="button"
          onClick={() =>
            onOpenBet({
              play_service_id: r.play_service_id!,
              game_uuid: r.game_uuid!,
              bet_id: r.bet_id!,
            })
          }
          className="ml-auto inline-flex items-center gap-1.5 border border-accent/40 bg-accent/10 px-3 py-1.5 font-condensed text-[11px] font-semibold uppercase tracking-wider text-accent hover:bg-accent/20"
        >
          <Telescope size={12} /> Inspect this bet
        </button>
      )}
    </section>
  );
}

/**
 * The one way this table is routinely misread — stated once, above the rows.
 *
 * ⚠️ In production essentially EVERY undelivered row carries a `delivered_at`
 * timestamp (it records the last attempt, not a success), so warning per row
 * would be noise on 100% of them. One note, shown only when such rows are
 * present, keeps the caveat impossible to miss without drowning the table.
 */
function MisreadNote({ rows }: { rows: DelayDeliveryTryRow[] }) {
  if (!rows.some((r) => !r.delivered && r.delivered_at)) return null;
  return (
    <section className="flex gap-2.5 border-b border-hairline bg-warn-bg px-4 py-2">
      <TriangleAlert size={13} className="mt-[2px] shrink-0 text-warn" />
      <p className="text-[12px] leading-relaxed text-text">
        Some rows below carry a <span className="font-mono">delivered_at</span> time while still
        marked <strong>undelivered</strong>. That timestamp is the last attempt, not a success —
        trust the <strong>state</strong> column, not the date.
      </p>
    </section>
  );
}

function Table({
  rows,
  tz,
  onOpenBet,
}: {
  rows: DelayDeliveryTryRow[];
  tz: TimeZoneMode;
  onOpenBet: (lookup: LookupParams) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="border-b border-hairline bg-surface">
            <Th />
            <Th>id</Th>
            <Th>
              <Explain term="action">action</Explain>
            </Th>
            <Th>state</Th>
            <Th align="right">
              <Explain term="delivery_tries">tries</Explain>
            </Th>
            <Th>
              <Explain term="delivery_last_try_result">last result</Explain>
            </Th>
            <Th>
              <Explain term="bet_id">bet</Explain>
            </Th>
            <Th>amount</Th>
            <Th>created</Th>
            <Th>last try</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <Row key={row.id} row={row} tz={tz} onOpenBet={onOpenBet} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Row({
  row,
  tz,
  onOpenBet,
}: {
  row: DelayDeliveryTryRow;
  tz: TimeZoneMode;
  onOpenBet: (lookup: LookupParams) => void;
}) {
  const [open, setOpen] = useState(false);
  const state = deliveryState(row);
  const payload = useMemo(() => payloadOf(row), [row]);
  const result = useMemo(() => lastResult(row), [row]);

  const created = parseMysqlTime(row.created_at);
  const lastTry = parseMysqlTime(row.last_delivery_try);
  const stuckFor = created && lastTry ? lastTry.getTime() - created.getTime() : null;

  const canOpen = Boolean(payload.gameUuid && payload.betId && row.play_service_id);

  return (
    <>
      <tr
        className={cn(
          'cursor-pointer border-b border-hairline/60 hover:bg-surface-raised',
          state === 'pending' && 'bg-crit-bg/30',
        )}
        onClick={() => setOpen((o) => !o)}
      >
        <td className="w-6 pl-4">
          <ChevronRight
            size={12}
            className={cn('text-text-faint transition-transform', open && 'rotate-90')}
          />
        </td>
        <td className="tnum px-3 py-1.5 font-mono text-text-dim">{row.id}</td>
        <td className="px-3 py-1.5">
          <span className="font-condensed text-[11px] font-semibold uppercase tracking-wider text-text">
            {row.action}
          </span>
        </td>
        <td className="px-3 py-1.5">
          <StateChip state={state} />
        </td>
        <td className="tnum px-3 py-1.5 text-right font-mono">
          <span className={row.tries > 3 ? 'text-warn' : 'text-text-dim'}>{row.tries}</span>
        </td>
        <td className="max-w-[260px] truncate px-3 py-1.5">
          {result.code ? (
            <span className="font-mono text-[11.5px] text-crit" title={result.message ?? ''}>
              {result.code}
            </span>
          ) : result.raw ? (
            <span className="font-mono text-[11.5px] text-text-dim">{result.raw.slice(0, 60)}</span>
          ) : (
            <span className="text-text-faint">—</span>
          )}
        </td>
        <td className="px-3 py-1.5">
          {payload.betId ? (
            <CopyId value={payload.betId} />
          ) : (
            <span className="text-text-faint">—</span>
          )}
        </td>
        <td className="tnum px-3 py-1.5 font-mono text-text-dim">
          {/* Payload amounts carry no currency_metadata, so they are shown as the
              producer wrote them with the code beside — never fake precision. */}
          {payload.amount ? `${payload.amount} ${payload.currency ?? ''}`.trim() : '—'}
        </td>
        <td className="px-3 py-1.5">
          <Timestamp date={created} raw={row.created_at} mode={tz} withDate />
        </td>
        <td className="px-3 py-1.5">
          <Timestamp date={lastTry} raw={row.last_delivery_try} mode={tz} withDate />
          {stuckFor !== null && stuckFor > 0 && (
            <span className="ml-2 text-[11px] text-text-faint">+{formatDuration(stuckFor)}</span>
          )}
        </td>
        <td className="px-3 py-1.5 text-right">
          {canOpen && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenBet({
                  play_service_id: row.play_service_id,
                  game_uuid: payload.gameUuid!,
                  bet_id: payload.betId!,
                });
              }}
              title="Open this bet in Bet Inspection"
              className="inline-flex items-center gap-1 border border-hairline px-1.5 py-[3px] font-condensed text-[10px] uppercase tracking-wider text-text-dim hover:border-accent/40 hover:text-accent"
            >
              <Telescope size={11} /> inspect
            </button>
          )}
        </td>
      </tr>

      {open && (
        <tr className="border-b border-hairline/60 bg-surface-sunken">
          <td colSpan={11} className="px-4 py-3">
            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <div className="eyebrow mb-1.5">
                  <Explain term="delivery_payload">payload — what we are delivering</Explain>
                </div>
                <JsonTree value={row.payload} defaultOpen={2} />
              </div>
              <div>
                <div className="eyebrow mb-1.5">
                  <Explain term="delivery_last_try_result">
                    last result — the operator's response
                  </Explain>
                </div>
                {result.parsed ? (
                  <JsonTree value={result.parsed} defaultOpen={2} />
                ) : result.raw ? (
                  // Not JSON: show it verbatim rather than pretending to parse it.
                  <pre className="whitespace-pre-wrap break-all font-mono text-[11.5px] text-text-dim">
                    {result.raw}
                  </pre>
                ) : (
                  <p className="text-[12px] text-text-dim">
                    No response recorded for the last attempt.
                  </p>
                )}

                <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 border-t border-hairline pt-2 text-[11.5px]">
                  <Kv label={<Explain term="tr_uniq_id">tr_uniq_id</Explain>}>
                    <CopyId value={row.tr_uniq_id} truncate />
                  </Kv>
                  <Kv label={<Explain term="cycle_uniq_id">cycle_uniq_id</Explain>}>
                    <CopyId value={row.cycle_uniq_id} truncate />
                  </Kv>
                  <Kv label={<Explain term="play_service_id">project</Explain>}>
                    <CopyId value={row.play_service_id} truncate />
                  </Kv>
                  <Kv label={<Explain term="delivery_delivered">delivered_at</Explain>}>
                    <Timestamp
                      date={parseMysqlTime(row.delivered_at)}
                      raw={row.delivered_at}
                      mode={tz}
                      withDate
                    />
                    {!row.delivered && row.delivered_at && (
                      <span className="text-[10.5px] uppercase tracking-wider text-warn">
                        not delivered
                      </span>
                    )}
                  </Kv>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function StateChip({ state }: { state: DeliveryState }) {
  const meta: Record<DeliveryState, { label: string; cls: string; term: string }> = {
    delivered: { label: 'delivered', cls: 'border-ok/40 text-ok', term: 'delivery_delivered' },
    pending: { label: 'undelivered', cls: 'border-crit/40 text-crit', term: 'delivery_delivered' },
    archived: { label: 'archived', cls: 'border-hairline text-text-faint', term: 'delivery_archived' },
  };
  const m = meta[state];
  return (
    <Explain term={m.term} className="no-underline">
      <span
        className={cn(
          'inline-flex border px-1.5 py-[1px] font-condensed text-[10px] font-semibold uppercase tracking-wider',
          m.cls,
        )}
      >
        {m.label}
      </span>
    </Explain>
  );
}

function Empty({ result }: { result: DelayDeliveryResult }) {
  const r = result.resolved;
  // "Nothing to retry" and "we never looked" are different answers and must not
  // share a message.
  const body =
    r.matchedBy !== 'recent' && !r.found && r.queriedBy === 'none'
      ? 'That id does not resolve to any transaction, so there was nothing to look up. Check the id and the project.'
      : r.matchedBy !== 'recent' && !r.found
        ? 'No transaction matched that id, and the id itself has no retry rows. Nothing was queued for delayed delivery.'
        : 'No retry rows match these filters — nothing here was queued for delayed delivery. That is the good outcome.';

  return (
    <div className="flex min-h-[28vh] flex-col items-center justify-center gap-3 px-6 text-center">
      <RefreshCcwDot size={20} className="text-text-faint" />
      <p className="max-w-[460px] text-[13px] leading-relaxed text-text-dim">{body}</p>
    </div>
  );
}

function Cell({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="eyebrow mb-0.5">{label}</div>
      <div className="flex h-[22px] items-center">{children}</div>
    </div>
  );
}

function Kv({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="shrink-0 font-condensed text-[10px] uppercase tracking-wider text-text-faint">
        {label}
      </span>
      {children}
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={cn(
        'px-3 py-1.5 font-condensed text-[10px] font-semibold uppercase tracking-wider text-text-faint',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

function Scanning() {
  return (
    <div className="instrument-bg flex min-h-[40vh] flex-col items-center justify-center gap-4">
      <div className="lens-scan relative h-px w-[220px] overflow-hidden bg-hairline" />
      <p className="font-condensed text-[11px] uppercase tracking-[0.2em] text-text-dim">
        reading the delivery retry queue
      </p>
    </div>
  );
}

function Idle() {
  return (
    <div className="instrument-bg flex min-h-[40vh] flex-col items-center justify-center gap-3 px-6 text-center">
      <RefreshCcwDot size={22} className="text-text-faint" />
      <p className="max-w-[480px] text-[13px] leading-relaxed text-text-dim">
        Wins and rollbacks that could not be delivered to the operator are queued and retried in
        the background. Pick a project to see the most recent ones, or enter any transaction id to
        see every retry attempt for that bet.
      </p>
    </div>
  );
}

function Failure({ error }: { error: Error }) {
  const status = error instanceof ApiError ? error.status : 0;
  return (
    <div className="flex min-h-[30vh] items-start justify-center px-6 pt-12">
      <div className="flex max-w-[520px] gap-3 border border-crit/40 bg-crit-bg p-4">
        <AlertCircle size={16} className="mt-[2px] shrink-0 text-crit" />
        <div>
          <div className="mb-1 font-condensed text-[11px] font-semibold uppercase tracking-wider text-crit">
            {status === 403 ? 'Not permitted' : 'Lookup failed'}
          </div>
          <p className="text-[12.5px] leading-relaxed text-text">{error.message}</p>
        </div>
      </div>
    </div>
  );
}
